<template>
  <div class="panel-card">
    <el-button type="primary" @click="openDialog" :icon="Plus">{{ $str.getKey('设备升级/新增标签') }}</el-button>
  </div>

  <div class="panel-card">
    <el-table :data="list">
      <el-table-column :label="$str.getKey('设备升级/标签')" prop="tagName"></el-table-column>
      <el-table-column :label="$str.getKey('设备升级/关联电站') + '(ID)'" prop="stationIds">
        <template #default="{ row }">
          <span v-if="row.stationIds.length > 0">
            {{ getStationIds(row.stationIds) }}
          </span>
          <span v-else>--</span>
        </template>
      </el-table-column>
      <el-table-column :label="$str.getKey('设备升级/备注')" prop="remark" :formatter="defaultFormatter" />
      <el-table-column :label="$str.getKey('设备升级/创建者')" prop="createBy"></el-table-column>
      <el-table-column :label="$str.getKey('设备升级/创建时间')" prop="createTime"></el-table-column>
      <el-table-column :label="$str.getKey('设备升级/操作')" width="200">
        <template #default="{ row }">
          <el-button type="primary" @click="edit(row)" text>{{ $str.getKey('设备升级/编辑') }}</el-button>
          <el-button type="danger" @click="del(row)" text>{{ $str.getKey('设备升级/删除') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <Pagination v-model="pageNum" :total="total" :page-size="pageSize" @change="fetchList" />
  </div>

  <el-dialog v-model="visible" :title="dialog_title" :close-on-press-escape="false" :close-on-click-modal="false" draggable
    class="ota-dialog">
    <el-form label-width="120px" :disabled="isConfirming" ref="formRef" :model="formData" :rules="rules" label-position="top">
      <el-form-item :label="$str.getKey('设备升级/标签名称')" prop="tagName">
        <el-input type="text" :placeholder="$str.getKey('设备升级/请输入标签名称')" v-model="tagName" maxlength="50" />
      </el-form-item>
      <el-form-item :label="$str.getKey('设备升级/关联电站')" prop="selectedStation">
        <div style="width: 100%;">
          <div class="flex">
            <el-checkbox @change="switchCheckAll" v-model="isCheckAll" :indeterminate="isIndeterminate">{{ $str.getKey('设备升级/全选') }}</el-checkbox>
            <el-input type="text" v-model="searchKey" :placeholder="$str.getKey('设备升级/搜索电站')"
              style="width: 100%;margin-left: 10px;"></el-input>
          </div>
          <el-checkbox-group v-model="selectedStation" class="tag-station-list" @change="selectedkeyChange">
            <el-checkbox class="tag-station-item" :label="station.stationId" :key="station.stationId"
              v-for="station in localStationList">
              <keyword-text :text="`[${station.stationId}]${station.name}`" :keyword="searchKey"></keyword-text>
            </el-checkbox>
          </el-checkbox-group>
        </div>
      </el-form-item>

      <el-form-item :label="$str.getKey('设备升级/备注')">
        <el-input type="textarea" v-model="note" />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="onCancel" :disabled="isConfirming">{{ $str.getKey('设备升级/取消') }}</el-button>
      <el-button type="primary" @click="onConfirm(formRef)" :loading="isConfirming">{{ $str.getKey('设备升级/确定') }}</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { eui } from 'ems-ui'
import { ElMessageBox } from 'element-plus'
import { getTags, createTag, modifyTag, delTag } from '@/api/upgrade'
import { useList } from './feature'

import useAppStore from '@/store/modules/app'
import KeywordText from "@/components/KeywordText"
import useUserStore from '@/store/modules/user'
import { Plus } from '@element-plus/icons-vue'
import Pagination from '@/components/Pagination/index.vue'

const userStore = useUserStore()
const appStore = useAppStore()
const searchKey = ref('')
const visible = ref(false)
const selectedStation = ref([])
const tagName = ref('')
const note = ref('')
const dialog_role = ref('add')
const formRef = ref(null)
const isCheckAll = ref(false)
const isIndeterminate = ref(false)
const $str = window.$str

const formData = reactive({
  tagName,
  note,
  selectedStation,
})

const rules = reactive({
  tagName: [
    { required: true, message: $str.getKey('设备升级/请输入标签名称'), trigger: 'blur' },
  ],
  selectedStation: [
    {
      type: 'array',
      required: true,
      message: $str.getKey('设备升级/请至少选择一个电站'),
      trigger: 'change',
    },
  ]
})

const stationList = computed(() => {
  return appStore.stations
})

// 改为 computed：原先写成 ref(appStore.stations) 是 setup 期的一次性快照，
// 而 getStations() 是整体替换数组（this.stations = res.data），
// 导致电站数据晚于本组件挂载到达时，弹窗里的电站复选框列表永远为空，
// 只有在搜索框里输入才会被 onSearchKeyChange 重新赋值。
const localStationList = computed(() => {
  const key = searchKey.value
  if (!key) return stationList.value
  return stationList.value.filter(s => `[${s.stationId}]${s.name}`.includes(key))
})

const dialog_title = computed(() => {
  const table = {
    add: $str.getKey('设备升级/添加'),
    edit: $str.getKey('设备升级/编辑'),
  }
  return table[dialog_role.value] + ' ' + $str.getKey('设备升级/标签')
})

function openDialog() {
  visible.value = true
  dialog_role.value = 'add'
  selectedStation.value = []
  tagName.value = ''
  note.value = ''
  isIndeterminate.value = false
  isCheckAll.value = false
}

function onCancel() {
  visible.value = false
}

function getStationIds(stationIds) {
  return stationIds.length > 3 ? stationIds.slice(0, 3).join(',') + '...' : stationIds.join(',')
}

const isConfirming = ref(false)

function onConfirm(formComp) {
  formComp.validate((valid) => {
    if (valid) {
      isConfirming.value = true
      if (dialog_role.value === 'add') {
        newTag()
      } else if (dialog_role.value === 'edit') {
        editTag()
      }
    }
  })
}

async function newTag() {
  try {
    await createTag(tagName.value, note.value, userStore.name, selectedStation.value)
    visible.value = false
    eui.showTopTip($str.getKey('设备升级/创建成功'), 'success')
    fetchList()
  } catch (error) {
    console.warn(error)
  } finally {
    isConfirming.value = false
  }
}

async function editTag() {
  try {
    await modifyTag(tagId, tagName.value, note.value, userStore.name, selectedStation.value)
    visible.value = false
    eui.showTopTip($str.getKey('设备升级/编辑成功'), 'success')
    fetchList()
  } catch (error) {
    console.warn(error)
  } finally {
    isConfirming.value = false
  }
}

/**
 * 全选/反选电站
 */
function switchCheckAll(selectAll) {
  let ids = stationList.value.map(item => item.stationId)
  if (selectAll) {
    selectedStation.value = ids
  } else {
    selectedStation.value = []
  }
  isIndeterminate.value = false
}

const { list, pageNum, pageSize, total, fetchList } = useList(({ pageNum, pageSize }) => {
  return getTags(null, pageNum, pageSize)
})

let tagId = null
function edit(row) {
  tagId = row.id
  tagName.value = row.tagName
  note.value = row.remark
  selectedStation.value = row.stationIds
  dialog_role.value = 'edit'
  visible.value = true

  if (selectedStation.value.length < stationList.value.length) {
    isIndeterminate.value = true
    isCheckAll.value = false
  } else {
    isIndeterminate.value = false
    isCheckAll.value = selectedStation.value.length === stationList.value.length
  }
  selectedkeyChange()
}

function selectedkeyChange() {
  if (selectedStation.value.length === stationList.value.length) {
    isIndeterminate.value = false
    isCheckAll.value = true
  } else if (selectedStation.value.length === 0) {
    isIndeterminate.value = false
    isCheckAll.value = false
  } else if (selectedStation.value.length < stationList.value.length) {
    isIndeterminate.value = true
    isCheckAll.value = false
  }
}

function del(row) {
  ElMessageBox.confirm($str.getKey('设备升级/确认删除') + '？', $str.getKey('设备升级/提示'), {
    confirmButtonText: $str.getKey('设备升级/确定'),
    cancelButtonText: $str.getKey('设备升级/取消'),
    type: 'warning'
  }).then(() => {
    delTag(row.id).then(() => {
      eui.showTopTip($str.getKey('设备升级/成功'), 'success')
      fetchList()
    }).catch(e => console.warn(e))
  }).catch(() => { })
}

function defaultFormatter(row, col, value) {
  return value || '--'
}
</script>

<style lang='scss' src="./style.scss"></style>

<style lang="scss" scoped>
.tag-station-list {
  height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  padding: 10px;
  margin-top: 10px;
}

.tag-station-item {
  margin: 5px 0;
}

.flex {
  display: flex;
  align-items: center;
}
</style>
